import { useState, useEffect, useMemo } from "react";
import { useFilterContext } from "./ContextManager";

import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";

import style from "../css/modalAirportsRoutes.module.css";

export const Modal = () => {
  const {
    filteredAirportsResult,
    selectedAirports,
    modalOpenState,
    updateModalState,
  } = useFilterContext();

  const [hasOpened, setHasOpened] = useState(false);
  const [modalClass, setModalClass] = useState(
    `${style.modal} ${style.hidden}`
  );

  // Airport actually shown in the modal (persists during close animation)
  const [activeAirportId, setActiveAirportId] = useState(null);

  // If user closes the modal for a given airport, we store its id here
  // so we don't auto-open again until the selection changes.
  const [suppressedAirportId, setSuppressedAirportId] = useState(null);

  // Derived single-selection for modal purposes
  const singleSelectedAirport = useMemo(() => {
    return selectedAirports && selectedAirports.length === 1
      ? selectedAirports[0]
      : null;
  }, [selectedAirports]);

  // Open/close modal based on single selection,
  // but don't auto-open if user has manually closed it for this airport.
  useEffect(() => {
    if (singleSelectedAirport) {
      const id = singleSelectedAirport.id;

      // Clear suppression if we're looking at a different airport
      if (suppressedAirportId && suppressedAirportId !== id) {
        setSuppressedAirportId(null);
      }

      // If this airport is currently suppressed, don't auto-open
      if (suppressedAirportId === id) {
        return;
      }

      // Set active airport if needed
      if (id !== activeAirportId) {
        setActiveAirportId(id);
      }

      // Open modal if it's not already open
      if (!modalOpenState) {
        updateModalState(true);
        setHasOpened(true);
      }
    } else {
      // Selection is 0 or >1: close modal and clear suppression,
      // so next time we select a single airport, it can open again.
      if (modalOpenState) {
        updateModalState(false);
      }
      if (suppressedAirportId) {
        setSuppressedAirportId(null);
      }
      // activeAirportId is cleared after close animation (below)
    }
  }, [
    singleSelectedAirport,
    activeAirportId,
    suppressedAirportId,
    modalOpenState,
    updateModalState,
  ]);

  // CSS animation class handling + clear activeAirportId after close animation
  useEffect(() => {
    if (modalOpenState) {
      setModalClass(`${style.modal} ${style.open}`);
      setHasOpened(true);
    } else if (hasOpened) {
      setModalClass(`${style.modal} ${style.close}`);

      const timer = setTimeout(() => {
        setModalClass(`${style.modal} ${style.close} ${style.hidden}`);
        setActiveAirportId(null); // Clear content only after animation
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [modalOpenState, hasOpened]);

  // Resolve the full airport object from activeAirportId
  const airport = useMemo(() => {
    if (!activeAirportId) return null;
    return (
      filteredAirportsResult &&
      filteredAirportsResult.find((a) => a.airportId === activeAirportId)
    );
  }, [activeAirportId, filteredAirportsResult]);

  // Safety: if modal is open but airport lookup failed, close it
  useEffect(() => {
    if (modalOpenState && activeAirportId && !airport) {
      updateModalState(false);
    }
  }, [modalOpenState, activeAirportId, airport, updateModalState]);

  let array = [];
  if (airport) {
    if (airport.municipality) array.push(airport.municipality);
    if (airport.country?.name) array.push(airport.country.name);
  }
  const airportLocation = array.join(", ");

  const handleCloseModal = () => {
    // User explicitly wants it hidden, but we keep selection.
    if (activeAirportId) {
      setSuppressedAirportId(activeAirportId);
    }
    updateModalState(false);
  };

  return (
    <div className={modalClass}>
      <div className={style.closeButton}>
        <IconButton
          className={style.modalIconButton}
          aria-label="close modal"
          onClick={handleCloseModal}
        >
          <HighlightOffIcon className={style.modalIcon} />
        </IconButton>
      </div>
      <div className={style.wrapper}>
        <div className={style.modalContent}>
          <div className={style.airportNameContainer}>
            {airport && airport.name && (
              <h4 className={style.airportName}>{airport.name}</h4>
            )}
          </div>
          <div className={style.modalInfo}>
            {airport && (
              <>
                <div>
                  {airport.type !== "custom" &&
                    (airport.country?.name || airport.municipality) && (
                      <div className={style.airportStats}>
                        {(airport.airportId || airport.iataCode) && (
                          <p className={style.statsValue}>
                            {airport.airportId || ""}
                            {airport.iataCode
                              ? `${airport.airportId ? " | " : ""}${
                                  airport.iataCode
                                }`
                              : ""}
                          </p>
                        )}
                        <p className={style.statsValue}>{airportLocation}</p>
                      </div>
                    )}
                  {airport.type && airport.type === "seaplane" && (
                    <div className={style.airportStats}>
                      <p className={style.statsValue}>{airport.type} Base</p>
                    </div>
                  )}
                  {airport.type && airport.type !== "seaplane" && (
                    <div className={style.airportStats}>
                      <p className={style.statsValue}>{airport.type} Airport</p>
                    </div>
                  )}
                  {airport.pax && (
                    <div className={`${style.airportStats} ${style.statsItem}`}>
                      <p className={style.statsValue}>
                        {airport.pax.toLocaleString()} passengers/year
                      </p>
                    </div>
                  )}
                  {airport.tour && (
                    <div className={`${style.airportStats} ${style.statsItem}`}>
                      <p className={style.statsValue}>Tours available</p>
                    </div>
                  )}
                </div>

                {airport.runways && airport.runways.length > 0 && (
                  <div>
                    <h4 className={style.runwaysTitle}>Runways</h4>
                    <ul className={style.runwaysList}>
                      {airport.runways.map((runway, i) => (
                        <li
                          key={i}
                          className={`${style.airportStats} ${style.runwayListItem}`}
                        >
                          <span className={style.statsRunway}>
                            {runway.length_m} m
                          </span>
                          <span className={style.statsValue}>
                            {" "}
                            - {runway.surface.replace("_", " ")}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {(airport.homepage || airport.wiki) && (
                  <div className={style.linkButtonContainer}>
                    {airport.homepage && (
                      <Button
                        variant="contained"
                        href={airport.homepage}
                        target="_blank"
                        size="small"
                      >
                        Homepage
                      </Button>
                    )}

                    {airport.wiki && (
                      <Button
                        variant="contained"
                        href={airport.wiki}
                        target="_blank"
                        size="small"
                      >
                        Wikipedia
                      </Button>
                    )}
                  </div>
                )}

                {airport.description && (
                  <div className={style.description}>{airport.description}</div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
